import React, { useState, useRef } from 'react';
import { 
  Button, 
  Input, 
  Select, 
  SelectItem, 
  Textarea, 
  Card, 
  CardBody,
  Switch,
  Chip
} from '@nextui-org/react';
import { 
  FiUpload, 
  FiX, 
  FiPlus, 
  FiTrash2, 
  FiSave,
  FiArrowLeft
} from 'react-icons/fi';
import axios from "axios";
import { product_uri } from '../../../utils/url';
import { toast } from "react-toastify";
import Loading from '../../../components/Loading';
import { PROD_TYPES, PRODUCT_TYPES } from '../../../utils/types';

const ProductAdd = () => {
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productType, setProductType] = useState('ready');
  const [type, setType] = useState('');
  const [estimatedDays, setEstimatedDays] = useState('');
  const [stockQuantity, setStockQuantity] = useState('');
  const [endDate, setEndDate] = useState();
  const [images, setImages] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [currentSize, setCurrentSize] = useState('');
  const [currentColor, setCurrentColor] = useState('');
  const [currentColorHex, setCurrentColorHex] = useState('#000000');
  const [isFormValid, setIsFormValid] = useState(false);
  const [load, setLoad] = useState(false);

  const fileInputRef = useRef(null);

  // Validate form when values change
  React.useEffect(() => {
    const valid = 
      productName.trim() !== '' && 
      productDescription.trim() !== '' && 
      productPrice.trim() !== '' && 
      images.length > 0 &&
      (
        (productType === PRODUCT_TYPES.BELEN && stockQuantity.trim() !== '') || 
        (productType === PRODUCT_TYPES.ZAHIALGA && estimatedDays.trim() !== '')
      );
    
    setIsFormValid(valid);
  }, [productName, productDescription, productPrice, images, sizes, colors, productType, type, stockQuantity, estimatedDays]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (images.length + files.length > 6) {
      alert('Та хамгийн ихдээ 6 зураг оруулах боломжтой');
      return;
    }
    
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    
    setImages([...images, ...newImages]);
  };

  const removeImage = (indexToRemove) => {
    setImages(images.filter((_, index) => index !== indexToRemove));
  };

  const addSize = () => {
    if (currentSize && !sizes.includes(currentSize)) {
      setSizes([...sizes, currentSize]);
      setCurrentSize('');
    }
  };

  const removeSize = (sizeToRemove) => {
    setSizes(sizes.filter(size => size !== sizeToRemove));
  };

  const addColor = () => {
    if (currentColor && !colors.some(color => color.name === currentColor)) {
      setColors([...colors, { name: currentColor, hex: currentColorHex }]);
      setCurrentColor('');
      setCurrentColorHex('#000000');
    }
  };

  const removeColor = (colorNameToRemove) => {
    setColors(colors.filter(color => color.name !== colorNameToRemove));
  };

  const handleSubmit = async (e) => {
    setLoad(true);
    e.preventDefault();
    
    if (!isFormValid) return;
    
    // Create form data to send to server (including images)
    const formData = new FormData();
    formData.append('name', productName);
    formData.append('desc', productDescription);
    formData.append('price', productPrice);
    formData.append('type', productType);
    formData.append('category', type);
    formData.append('end_date', endDate);
    
    if (productType === PRODUCT_TYPES.BELEN) {
      formData.append('stockQuantity', stockQuantity);
    } else {
      formData.append('estimatedDays', estimatedDays);
    }
    
    // Append all sizes
    sizes.forEach((size, index) => {
      formData.append(`sizes[${index}]`, size);
    });
    
    // Append all colors
    colors.forEach((color, index) => {
      formData.append(`colors[${index}][name]`, color.name);
      formData.append(`colors[${index}][hex]`, color.hex);
    });
    
    // Append all images
    images.forEach((image, index) => {
      formData.append(`images`, image.file);
    });
    
    try {

      const response = await fetch(product_uri, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
          toast.success("Амжилттай бараа нэмэгдсэн");
          setProductName('')
          setProductDescription('')
          setProductPrice('')
          setType('')
          setImages([])
          setSizes([])
          setColors([])
          setProductType('')
          setStockQuantity('')
          setEstimatedDays('')
          setLoad(false);
      } else {
        setLoad(false);
          console.error('Upload failed');
      } 
      
    } catch (error) {
      setLoad(false);
      console.error('Error submitting form:', error);
      alert('Алдаа гарлаа: ' + error.message);
    }
  };

  return (
    <div className="">
      {
        load &&
        <Loading/>
      }
      <div className="flex items-center mb-6">
        <Button 
          variant="light" 
          startContent={<FiArrowLeft />}
          className="mr-4"
        >
          Буцах
        </Button>
        <h1 className="text-2xl font-bold">Шинэ бараа нэмэх</h1>
      </div>
      
      <form onSubmit={handleSubmit}>
        <Card className="mb-6">
          <CardBody>
            <h2 className="text-xl font-semibold mb-4">Үндсэн мэдээлэл</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Input
                label="Барааны нэр"
                placeholder="Жишээ: Nike Air Max"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                isRequired
              />
              
              <Input
                label="Үнэ (₮)"
                placeholder="Жишээ: 25000"
                type="number"
                value={productPrice}
                onChange={(e) => setProductPrice(e.target.value)}
                startContent={
                  <div className="pointer-events-none flex items-center">
                    <span className="text-default-400 text-small">₮</span>
                  </div>
                }
                isRequired
              />
            </div>
            
            <Textarea
              label="Барааны тайлбар"
              placeholder="Барааны дэлгэрэнгүй тайлбар..."
              value={productDescription}
              onChange={(e) => setProductDescription(e.target.value)}
              className="mb-4"
              minRows={3}
              isRequired
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Барааны төрөл"
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                isRequired
              >
                <SelectItem key={PRODUCT_TYPES.BELEN} value={PRODUCT_TYPES.BELEN}>Бэлэн бараа</SelectItem>
                <SelectItem key={PRODUCT_TYPES.ZAHIALGA} value={PRODUCT_TYPES.ZAHIALGA}>Захиалгын бараа</SelectItem>
              </Select>
              
              <Select
                label="Category"
                value={type}
                onChange={(e) => setType(e.target.value)}
                isRequired
              >
                <SelectItem key={PROD_TYPES.AYLAL} value={PRODUCT_TYPES.AYLAL}>Аялалын хэрэгсэл</SelectItem>
                <SelectItem key={PROD_TYPES.TSAHILGAAN} value={PRODUCT_TYPES.TSAHILGAAN}>Цахилгаан хэрэгсэл</SelectItem>
                <SelectItem key={PROD_TYPES.HUUHED_HEREGSEL} value={PRODUCT_TYPES.HUUHED_HEREGSEL}>Хүүхдийн хэрэгсэл</SelectItem>
                <SelectItem key={PROD_TYPES.HUUHED_HUVTSAS} value={PRODUCT_TYPES.HUUHED_HUVTSAS}>Хүүхдийн хувцас</SelectItem>
                <SelectItem key={PROD_TYPES.GOYL} value={PRODUCT_TYPES.GOYL}>Гоёл  чимэглэл</SelectItem>
                <SelectItem key={PROD_TYPES.GER} value={PRODUCT_TYPES.GER}>Гэр ахуй</SelectItem>
                <SelectItem key={PROD_TYPES.BELEN_CLOTHS} value={PRODUCT_TYPES.BELEN_CLOTHS}>Бэлэн хувцас</SelectItem>
              </Select>
              
              {productType === PRODUCT_TYPES.BELEN ? (
                <Input
                  label="Нөөц тоо"
                  placeholder="Жишээ: 10"
                  type="number"
                  value={stockQuantity}
                  onChange={(e) => setStockQuantity(e.target.value)}
                  isRequired
                />
              ) : (
                <Input
                  label="Ойролцоо хүргэлтийн хугацаа (өдөр)"
                  placeholder="Жишээ: 14"
                  type="number"
                  value={estimatedDays}
                  onChange={(e) => setEstimatedDays(e.target.value)}
                  isRequired
                />
              )}
            </div>
            <div className='mt-4'>
              {
                productType === PRODUCT_TYPES.ZAHIALGA &&
                <Input
                  className='w-48'
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              }
            </div>
          </CardBody>
        </Card>
        
        <Card className="mb-6">
          <CardBody>
            <h2 className="text-xl font-semibold mb-4">Барааны зургууд</h2>
            <p className="text-sm text-gray-500 mb-4">Хамгийн ихдээ 6 зураг оруулах боломжтой</p>
            
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-4">
              {images.map((image, index) => (
                <div key={index} className="relative rounded-lg overflow-hidden border border-gray-200 aspect-square">
                  <img 
                    src={image.preview} 
                    alt={`Product ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                  >
                    <FiX size={16} />
                  </button>
                </div>
              ))}
              
              {images.length < 6 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center p-4 hover:bg-gray-50 aspect-square"
                >
                  <FiUpload size={24} className="text-gray-400 mb-2" />
                  <span className="text-xs text-gray-500">Зураг нэмэх</span>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    multiple
                    accept="image/*"
                    className="hidden"
                  />
                </button>
              )}
            </div>
            
            {images.length === 0 && (
              <div className="text-center p-6 border-2 border-dashed border-gray-300 rounded-lg">
                <FiUpload size={36} className="mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500 mb-2">Зургаа энд чирэх эсвэл дарж оруулна уу</p>
                <Button 
                  variant="flat" 
                  startContent={<FiPlus />}
                  onClick={() => fileInputRef.current.click()}
                >
                  Зураг сонгох
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
        
        <Card className="mb-6">
          <CardBody>
            <h2 className="text-xl font-semibold mb-4">Хэмжээ ба Өнгө</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Хэмжээ</h3>
                <div className="flex gap-2 mb-3">
                  <Input
                    size='xs'
                    placeholder="Шинэ хэмжээ (жишээ: 42)"
                    value={currentSize}
                    onChange={(e) => setCurrentSize(e.target.value)}
                    className="flex-grow"
                  />
                  <Button
                    type="button"
                    onClick={addSize}
                    isDisabled={!currentSize}
                    startContent={<FiPlus />}
                  >
                    Нэмэх
                  </Button>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-2">
                  {sizes.map((size) => (
                    <Chip
                      key={size}
                      onClose={() => removeSize(size)}
                      variant="flat"
                      className="bg-gray-100"
                    >
                      {size}
                    </Chip>
                  ))}
                </div>
                {sizes.length === 0 && (
                  <p className="text-sm text-gray-500">Хэмжээ оруулна уу</p>
                )}
              </div>
              
              <div>
                <h3 className="text-lg font-medium mb-2">Өнгө</h3>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Өнгөний нэр (жишээ: Хар)"
                    value={currentColor}
                    onChange={(e) => setCurrentColor(e.target.value)}
                    className="flex-grow"
                    size='xs'
                  />
                  <Input
                    type="color"
                    aria-label="Color picker"
                    value={currentColorHex}
                    onChange={(e) => setCurrentColorHex(e.target.value)}
                    className="w-16"
                    size='xs'
                  />
                  <Button
                    type="button"
                    onClick={addColor}
                    isDisabled={!currentColor}
                    startContent={<FiPlus />}
                  >
                    Нэмэх
                  </Button>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-2">
                  {colors.map((color) => (
                    <Chip
                      key={color.name}
                      onClose={() => removeColor(color.name)}
                      variant="flat"
                      className="bg-gray-100"
                      startContent={
                        <div 
                          className="w-3 h-3 rounded-full mr-1" 
                          style={{ backgroundColor: color.hex }}
                        />
                      }
                    >
                      {color.name}
                    </Chip>
                  ))}
                </div>
                {colors.length === 0 && (
                  <p className="text-sm text-gray-500">Өнгө оруулна уу</p>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
        
        <div className="flex justify-between items-center">
          <Button
            type="button"
            variant="flat"
            color="danger"
            startContent={<FiTrash2 />}
          >
            Цуцлах
          </Button>
          
          <Button
            type="submit"
            color="primary"
            startContent={<FiSave />}
            isDisabled={!isFormValid}
          >
            Бараа нэмэх
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProductAdd;